import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import type { ResourceField, ResourceFieldType, ResourceAggregation } from '../../types';

const FIELD_TYPES: ResourceFieldType[] = ['text', 'number', 'currency', 'percentage', 'duration'];
const AGGREGATIONS: ResourceAggregation[] = ['sum', 'avg', 'min', 'max', 'none'];

export function ResourceFieldEditor({
  field,
  onUpdate,
  onRemove,
}: {
  field: ResourceField;
  onUpdate: (updates: Partial<ResourceField>) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="border border-gray-200 rounded p-2 bg-gray-50">
      {/* Header row: field name + remove */}
      <div className="flex items-center gap-2 mb-1.5">
        <input
          type="text"
          value={field.key}
          onChange={(e) => onUpdate({ key: e.target.value })}
          placeholder={t('resource.field.fieldName')}
          className="flex-1 px-1.5 py-0.5 border border-gray-200 rounded text-[10px] bg-white text-gray-900"
        />
        <button
          onClick={onRemove}
          className="text-gray-400 hover:text-red-600 transition-colors"
          title={t('resource.field.removeField')}
        >
          <X size={12} />
        </button>
      </div>

      {/* Type + Value row */}
      <div className="flex gap-1.5 mb-1.5">
        <select
          value={field.type}
          onChange={(e) => onUpdate({ type: e.target.value as ResourceFieldType })}
          className="px-1 py-0.5 border border-gray-200 rounded text-[10px] bg-white text-gray-700"
        >
          {FIELD_TYPES.map(ft => (
            <option key={ft} value={ft}>{t(`resource.field.types.${ft}` as any)}</option>
          ))}
        </select>
        <input
          type={field.type === 'text' ? 'text' : 'number'}
          value={field.value}
          onChange={(e) => onUpdate({ value: field.type === 'text' ? e.target.value : Number(e.target.value) || 0 })}
          placeholder={t('resource.field.fieldValue')}
          className="flex-1 px-1.5 py-0.5 border border-gray-200 rounded text-[10px] bg-white text-gray-900"
        />
      </div>

      {/* Unit + Interval + Aggregate row */}
      <div className="flex gap-1.5 mb-1.5">
        <input
          type="text"
          value={field.unit || ''}
          onChange={(e) => onUpdate({ unit: e.target.value || undefined })}
          placeholder={t('resource.field.fieldUnit')}
          className="w-16 px-1.5 py-0.5 border border-gray-200 rounded text-[10px] bg-white text-gray-900"
        />
        {field.type === 'currency' && (
          <select
            value={field.interval || ''}
            onChange={(e) => onUpdate({ interval: (e.target.value || undefined) as any })}
            className="px-1 py-0.5 border border-gray-200 rounded text-[10px] bg-white text-gray-700"
          >
            <option value="">{t('resource.field.interval')}</option>
            <option value="monthly">{t('resource.field.intervals.monthly' as any)}</option>
            <option value="yearly">{t('resource.field.intervals.yearly' as any)}</option>
            <option value="hourly">{t('resource.field.intervals.hourly' as any)}</option>
          </select>
        )}
        <select
          value={field.aggregate}
          onChange={(e) => onUpdate({ aggregate: e.target.value as ResourceAggregation })}
          className="px-1 py-0.5 border border-gray-200 rounded text-[10px] bg-white text-gray-700"
        >
          {AGGREGATIONS.map(agg => (
            <option key={agg} value={agg}>{t(`resource.field.aggregations.${agg}` as any)}</option>
          ))}
        </select>
      </div>

      {/* Show on badge toggle */}
      <label className="flex items-center gap-1.5 cursor-pointer">
        <input
          type="checkbox"
          checked={field.showOnBadge}
          onChange={(e) => onUpdate({ showOnBadge: e.target.checked })}
          className="w-3 h-3 rounded border-gray-300"
        />
        <span className="text-[10px] text-gray-600">{t('resource.field.showOnBadge')}</span>
      </label>
    </div>
  );
}
